// Hybrid SSE + polling store for /User/GetUserinfo.
// Tries SSE first (server push, near-zero traffic when data is unchanged).
// Falls back to polling with ETag caching if SSE fails or disconnects.

let subscribers = new Map(); // cb -> intervalMs
let timer = null;
let currentInterval = null;
let lastData = null;
let lastEtag = null;
let inFlight = false;
let eventSource = null;
let sseConnected = false;
let sseRetryCount = 0;
const MAX_SSE_RETRIES = 3;

// ---- Broadcasting ----
const broadcast = (json) => {
  lastData = json;
  subscribers.forEach((_, cb) => {
    try { cb(json); } catch (e) { console.log('userInfo subscriber error:', e); }
  });
};

// ---- SSE ----
const connectSSE = () => {
  if (!localStorage.getItem('roles')) return;
  if (eventSource) { eventSource.close(); eventSource = null; }

  // SSE: EventSource can't set headers, so pass token as query param fallback.
  // The backend JwtRequestFilter checks: cookie → header → ?token= query param.
  let url = process.env.REACT_APP_IP + '/SSE/UserInfo';
  try {
    const { getToken } = require('./auth');
    const token = getToken();
    if (token) url += '?token=' + encodeURIComponent(token);
  } catch (e) { /* auth module not available */ }

  try {
    eventSource = new EventSource(url);

    eventSource.addEventListener('userinfo', (e) => {
      try {
        const json = JSON.parse(e.data);
        sseConnected = true;
        sseRetryCount = 0;
        broadcast(json);
        // SSE is working — stop polling
        stopPolling();
      } catch (err) {
        console.log('SSE parse error:', err);
      }
    });

    eventSource.onerror = () => {
      sseConnected = false;
      eventSource.close();
      eventSource = null;
      sseRetryCount++;
      if (sseRetryCount <= MAX_SSE_RETRIES) {
        // Retry SSE after delay
        setTimeout(connectSSE, 5000 * sseRetryCount);
      }
      // Start polling as fallback
      startPolling();
    };

    eventSource.onopen = () => {
      sseConnected = true;
      sseRetryCount = 0;
    };
  } catch (e) {
    console.log('SSE not supported, using polling fallback');
    startPolling();
  }
};

// ---- Polling fallback (with ETag) ----
const fetchUserInfo = async () => {
  if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
  if (!localStorage.getItem('roles')) return;
  if (inFlight) return;
  inFlight = true;
  try {
    const headers = {
      'Content-Type': 'application/json',
    };
    if (lastEtag) headers['If-None-Match'] = lastEtag;

    const res = await fetch(process.env.REACT_APP_IP + '/User/GetUserinfo', {
      method: 'POST',
      credentials: 'include',
      headers,
    });

    if (res.status === 304) return; // No change

    const etag = res.headers.get('ETag');
    if (etag) lastEtag = etag;

    const json = await res.json();
    broadcast(json);
  } catch (e) {
    console.log('userInfoStore fetch error:', e);
  } finally {
    inFlight = false;
  }
};

const minInterval = () => {
  let min = Infinity;
  subscribers.forEach((ms) => { if (ms < min) min = ms; });
  return min === Infinity ? null : min;
};

const startPolling = () => {
  if (sseConnected) return; // SSE active, no need for polling
  const ms = minInterval();
  currentInterval = ms;
  if (timer) clearInterval(timer);
  if (ms != null) timer = setInterval(fetchUserInfo, ms);
};

const stopPolling = () => {
  if (timer) { clearInterval(timer); timer = null; }
  currentInterval = null;
};

// ---- Public API ----
export const subscribeUserInfo = (cb, intervalMs = 10000) => {
  subscribers.set(cb, intervalMs);
  if (lastData) {
    try { cb(lastData); } catch (e) { console.log('userInfo subscriber error:', e); }
  }

  // First subscriber: try SSE, fallback to polling
  if (subscribers.size === 1) {
    connectSSE();
    // Also do one immediate fetch so we don't wait for SSE handshake
    fetchUserInfo();
  } else if (!sseConnected) {
    startPolling();
  }

  return () => {
    subscribers.delete(cb);
    if (subscribers.size === 0) {
      stopPolling();
      if (eventSource) { eventSource.close(); eventSource = null; sseConnected = false; }
    } else if (!sseConnected) {
      const newMin = minInterval();
      if (newMin !== currentInterval) startPolling();
    }
  };
};

export const refreshUserInfoNow = () => fetchUserInfo();

if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      if (sseConnected) return; // SSE is live, no action needed
      fetchUserInfo();
    }
  });
}
